import { useEffect, useState } from 'react'
import { Stack, Text, Tooltip } from '@mantine/core'
import { dayjs } from '../lib/dayjs'

/** How often "2 minutes ago" is recalculated while the page stays open. */
const TICK_MS = 30_000

/**
 * Re-renders on a shared interval so relative times stay honest.
 *
 * Without this, an entry rendered as "2 minutes ago" still says that twenty
 * minutes later - which is worse than showing no relative time at all, because
 * it looks precise while being wrong.
 */
function useTick(): number {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => setTick((value) => value + 1), TICK_MS)
    return () => window.clearInterval(id)
  }, [])

  return tick
}

/**
 * Absolute date and time, with how long ago it was underneath.
 *
 * Both, because they answer different questions: "2 minutes ago" is what you
 * want when scanning for something that just happened, and the exact stamp is
 * what you want the moment anything looks wrong.
 */
export function Timestamp({ value }: { value: string }) {
  useTick()

  const moment = dayjs(value)

  return (
    <Tooltip label={moment.format('dddd, D MMMM YYYY [at] HH:mm:ss')} openDelay={300} withArrow>
      <Stack gap={0} style={{ width: 'fit-content' }}>
        <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {moment.format('D MMM YYYY, HH:mm')}
        </Text>
        <Text size="xs" c="dimmed">
          {moment.fromNow()}
        </Text>
      </Stack>
    </Tooltip>
  )
}

/** The one-line form, for places too narrow for two. */
export function RelativeTime({ value }: { value: string }) {
  useTick()

  const moment = dayjs(value)

  return (
    <Tooltip label={moment.format('D MMM YYYY, HH:mm:ss')} openDelay={300} withArrow>
      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
        {moment.fromNow()}
      </Text>
    </Tooltip>
  )
}

/**
 * The one-line absolute form, with how long ago it was in the tooltip.
 *
 * The mirror of RelativeTime, for the places where the exact moment is the
 * answer and recency is the aside - a task's header, where "4 minutes ago"
 * stops being useful the day after it was written and never becomes useful
 * again.
 */
export function ExactTime({ value }: { value: string }) {
  useTick()

  const moment = dayjs(value)

  return (
    <Tooltip label={moment.fromNow()} openDelay={300} withArrow>
      <Text
        size="xs"
        c="dimmed"
        style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
      >
        {moment.format('D MMM YYYY, HH:mm')}
      </Text>
    </Tooltip>
  )
}
